import { useRef, useState } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import { ChevronDown, ChevronRight, ImagePlus, Loader2, X } from 'lucide-react';
import { useEventCoverUpload } from '@/lib/api/useEventCoverUpload';
import { useToast } from '@/components/ux/Toast';
import { hasPollAnswerDetails, type PollAnswerFormValue } from '../utils/pollAnswers';

interface Props {
  index: number;
  // Any form with an `answers: PollAnswerFormValue[]` field.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  value: PollAnswerFormValue;
  onImageChange: (url: string) => void;
}

/**
 * Collapsible optional fields for one poll option. Filling any of them makes
 * the poll render as option cards (image, subtitle, tags, blurb, link).
 */
export function PollAnswerDetailsFields({ index, register, value, onImageChange }: Props) {
  const [open, setOpen] = useState(() => hasPollAnswerDetails(value));
  const upload = useEventCoverUpload();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const field = (name: keyof PollAnswerFormValue) => register(`answers.${index}.${name}`);

  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) return toast.error('Image must be under 10MB');
    try {
      onImageChange(await upload.mutateAsync(file));
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    }
  };

  return (
    <div className="ml-7">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-dark-muted hover:text-primary-500"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Details {hasPollAnswerDetails(value) && !open && '•'}
      </button>
      {open && (
        <div className="flex gap-3 mt-2">
          {/* Image */}
          <div className="w-20 flex-shrink-0">
            {value.imageUrl ? (
              <div className="relative">
                <img src={value.imageUrl} alt="" className="w-20 aspect-square object-contain rounded-md bg-neutral-100 dark:bg-neutral-800" />
                <button
                  type="button"
                  onClick={() => onImageChange('')}
                  className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-black/70 text-white"
                  aria-label="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-20 aspect-square rounded-md border border-dashed border-neutral-300 dark:border-neutral-700 flex flex-col items-center justify-center gap-1 text-[10px] text-dark-muted hover:border-primary-500"
              >
                {upload.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                Image
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = '';
              }}
            />
          </div>
          {/* Text fields */}
          <div className="flex-1 grid gap-2 text-sm">
            <input {...field('subtitle')} className="input input-sm" placeholder="Subtitle (e.g. Author · 392 pages)" />
            <input {...field('tags')} className="input input-sm" placeholder="Tags, comma separated" />
            <textarea {...field('description')} rows={2} maxLength={500} className="textarea textarea-sm" placeholder="Short description" />
            <input {...field('linkUrl')} className="input input-sm" placeholder="Link for “More about it” (https://…)" />
          </div>
        </div>
      )}
    </div>
  );
}
